import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { buildPortfolioSnapshot, euAllergens } from "./compliance";
import { buildRegulatoryTrackerSnapshot } from "./regulatoryTracker";
import { buildIngredientExplorer } from "./ingredientExplorer";
import {
  createVendor,
  createVendorCertification,
  getPlatformState,
  recomputeRegulatoryImpacts,
  saveFormulaRun,
  syncAllRegulatorySources,
  syncRegulatorySource,
  updateJobStatus as updateJobStatusRequest,
  uploadDocuments,
  updateVendorFacility as updateVendorFacilityRequest,
  updateProductMocra as updateProductMocraRequest,
  addAdverseEvent as addAdverseEventRequest,
  updateAdverseEvent as updateAdverseEventRequest,
} from "../services/api";

const PlatformContext = createContext(null);

const emptyState = {
  products: [],
  documents: [],
  vendors: [],
  regulatoryUpdates: [],
  regulatorySources: [],
  regulatoryIngredients: [],
  regulatoryChanges: [],
  regulatoryRules: [],
  regulatoryRuleMetadata: {},
  jobs: [],
  workflows: [],
};

export const PlatformProvider = ({ children }) => {
  const [state, setState] = useState(emptyState);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const reload = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const nextState = await getPlatformState();
      setState(nextState);
    } catch (loadError) {
      setError(loadError.message || "Unable to load platform state.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const snapshot = useMemo(
    () =>
      buildPortfolioSnapshot(state.products, state.documents, {
        regulatoryIngredients: state.regulatoryIngredients,
        regulatoryChanges: state.regulatoryChanges,
        regulatorySources: state.regulatorySources,
        regulatoryRules: state.regulatoryRules,
        vendors: state.vendors,
      }),
    [
      state.documents,
      state.products,
      state.regulatoryChanges,
      state.regulatoryIngredients,
      state.regulatoryRules,
      state.regulatorySources,
      state.vendors,
    ]
  );

  const dashboard = useMemo(() => {
    const evaluatedProducts = snapshot.evaluatedProducts;
    const readyProducts = evaluatedProducts.filter(
      (product) => product.compliance.overallStatus === "Ready"
    ).length;
    const allIssues = snapshot.allIssues;
    const blockingIssues = allIssues.filter((issue) => issue.blocking);
    const expiringVendors = state.vendors.filter((vendor) =>
      vendor.certifications.some((cert) => cert.status === "Pending Renewal" || cert.status === "Missing")
    );

    return {
      products: evaluatedProducts.length,
      readyProducts,
      blockingIssues: blockingIssues.length,
      documents: state.documents.length,
      pendingDocuments: state.documents.filter((document) => document.status !== "Approved").length,
      vendors: state.vendors.length,
      expiringVendors: expiringVendors.length,
      jobsRunning: state.jobs.filter((job) => job.status === "Running").length,
      allIssues,
    };
  }, [snapshot, state.documents, state.jobs, state.vendors]);

  const regulatoryTracker = useMemo(
    () =>
      buildRegulatoryTrackerSnapshot({
        products: snapshot.evaluatedProducts,
        regulatorySources: state.regulatorySources,
        regulatoryIngredients: state.regulatoryIngredients,
        regulatoryChanges: state.regulatoryChanges,
        regulatoryRules: state.regulatoryRules,
      }),
    [
      snapshot.evaluatedProducts,
      state.regulatoryChanges,
      state.regulatoryIngredients,
      state.regulatoryRules,
      state.regulatorySources,
    ]
  );

  const ingredientExplorer = useMemo(
    () =>
      buildIngredientExplorer({
        regulatoryRules: state.regulatoryRules,
        regulatoryIngredients: state.regulatoryIngredients,
        euAllergens,
      }),
    [state.regulatoryRules, state.regulatoryIngredients]
  );

  const createFormulaRun = async (payload) => {
    const nextState = await saveFormulaRun(payload);
    setState(nextState);
    return nextState;
  };

  const addDocuments = async (payload) => {
    const nextState = await uploadDocuments(payload);
    setState(nextState);
  };

  const addVendor = async (payload) => {
    const nextState = await createVendor(payload);
    setState(nextState);
  };

  const addVendorCertification = async (payload) => {
    const nextState = await createVendorCertification(payload);
    setState(nextState);
  };

  const markJobStatus = async ({ jobId, status }) => {
    const nextState = await updateJobStatusRequest({ jobId, status });
    setState(nextState);
  };

  const runRegulatorySync = async (sourceType) => {
    const nextState = await syncRegulatorySource(sourceType);
    setState(nextState);
  };

  const refreshRegulatoryImpacts = async () => {
    const nextState = await recomputeRegulatoryImpacts();
    setState(nextState);
  };

  const runAllRegulatorySyncs = async () => {
    const nextState = await syncAllRegulatorySources();
    setState(nextState);
  };

  const updateVendorFacility = async (payload) => {
    const nextState = await updateVendorFacilityRequest(payload);
    setState(nextState);
    return nextState;
  };

  const updateProductMocra = async (payload) => {
    const nextState = await updateProductMocraRequest(payload);
    setState(nextState);
    return nextState;
  };

  const logAdverseEvent = async (payload) => {
    const nextState = await addAdverseEventRequest(payload);
    setState(nextState);
    return nextState;
  };

  const resolveAdverseEvent = async (payload) => {
    const nextState = await updateAdverseEventRequest(payload);
    setState(nextState);
    return nextState;
  };

  const value = {
    state,
    products: snapshot.evaluatedProducts,
    documents: state.documents,
    vendors: state.vendors,
    regulatoryUpdates: state.regulatoryUpdates,
    regulatorySources: state.regulatorySources,
    regulatoryIngredients: state.regulatoryIngredients,
    regulatoryChanges: state.regulatoryChanges,
    regulatoryRules: state.regulatoryRules,
    regulatoryRuleMetadata: state.regulatoryRuleMetadata,
    regulatoryTracker,
    ingredientExplorer,
    jobs: state.jobs,
    workflows: state.workflows,
    issues: dashboard.allIssues,
    dashboard,
    loading,
    error,
    reload,
    createFormulaRun,
    addDocuments,
    addVendor,
    addVendorCertification,
    markJobStatus,
    runRegulatorySync,
    runAllRegulatorySyncs,
    refreshRegulatoryImpacts,
    updateVendorFacility,
    updateProductMocra,
    logAdverseEvent,
    resolveAdverseEvent,
  };

  return <PlatformContext.Provider value={value}>{children}</PlatformContext.Provider>;
};

export const usePlatform = () => {
  const context = useContext(PlatformContext);
  if (!context) {
    throw new Error("usePlatform must be used within PlatformProvider");
  }
  return context;
};
